import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import BuyCredits from "./components/BuyCredits";
import Dashboard from "./components/Dashboard";
import Home from "./components/Home";
import PaymentSuccess from "./components/PaymentSuccess";
import PaymentCancel from "./components/PaymentCancel";
import Help from "./components/Help";
import AdminConsole from "./components/AdminConsole";
import "./App.css";
import { Button } from "./components/ui/button";
import { Sparkles, HelpCircle, Zap, LogOut, Menu, X } from "lucide-react";

// Use a relative URL when in production, allowing the Nginx proxy to handle routing
const API_URL =
  process.env.NODE_ENV === "production"
    ? "/api"
    : process.env.REACT_APP_API_URL || "http://localhost:8080/api";

// Function to check if the system prefers dark mode
function getSystemTheme() {
  if (typeof window !== "undefined") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  }
  return "light";
}

// ScrollToTop component - will scroll the window up on every navigation
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Navigation component to handle postMessage navigation
function NavigationHandler({ children }) {
  const navigate = useNavigate();

  useEffect(() => {
    const handleMessage = (event) => {
      // Make sure the message is from our own domain
      if (event.origin !== window.location.origin) return;

      if (event.data.type === "NAVIGATE_TO") {
        navigate(event.data.path);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [navigate]);

  return <>{children}</>;
}

function App() {
  const isAdminRoute =
    window.location.pathname === "/admin" ||
    window.location.pathname.startsWith("/admin/");
  const [apiKey, setApiKey] = useState(localStorage.getItem("apiKey") || "");
  const [quotaInfo, setQuotaInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Apply system theme preference for dark/light mode
  useEffect(() => {
    const systemTheme = getSystemTheme();
    document.documentElement.classList.add(systemTheme);

    // Add event listener for theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => {
      if (e.matches) {
        document.documentElement.classList.remove("light");
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
        document.documentElement.classList.add("light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Define fetchQuotaInfo with useCallback to prevent infinite loops
  const fetchQuotaInfo = useCallback(async () => {
    if (!apiKey) return;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API_URL}/quota`, {
        headers: {
          "X-API-Key": apiKey,
        },
      });

      setQuotaInfo(response.data);
    } catch (err) {
      setError("Failed to fetch quota information. Please check your API key.");
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  // Listen for API key updates via postMessage
  useEffect(() => {
    const handleMessage = (event) => {
      // Make sure the message is from our own domain
      if (event.origin !== window.location.origin) return;

      if (event.data.type === "API_KEY_UPDATED" && event.data.apiKey) {
        setApiKey(event.data.apiKey);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // Load quota information if API key exists
  useEffect(() => {
    if (apiKey) {
      fetchQuotaInfo();
    }
  }, [apiKey, fetchQuotaInfo]);

  const handleSaveApiKey = (key) => {
    setApiKey(key);
    localStorage.setItem("apiKey", key);
  };

  const handleLogout = () => {
    setApiKey("");
    setQuotaInfo(null);
    localStorage.removeItem("apiKey");
    setMobileMenuOpen(false);
  };

  return (
    <Router>
      <HelmetProvider>
        <NavigationHandler>
          <ScrollToTop />
          {isAdminRoute ? (
            <Routes>
              <Route path="/admin/*" element={<AdminConsole />} />
            </Routes>
          ) : (
          <div className="min-h-screen bg-background">
            {/* Modern Minimal Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/40">
              <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                  {/* Logo */}
                  <Link
                    to="/"
                    className="flex items-center space-x-2 text-xl font-semibold"
                  >
                    <div className="relative">
                      <Sparkles className="h-8 w-8 text-primary" />
                      <div className="absolute inset-0 blur-xl bg-primary/20"></div>
                    </div>
                    <span className="gradient-text font-bold">RecapMate</span>
                  </Link>

                  {/* Desktop Navigation */}
                  <div className="hidden md:flex items-center space-x-1">
                    <Link
                      to="/help"
                      className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted/50"
                    >
                      <HelpCircle className="w-4 h-4 inline mr-1.5" />
                      Help
                    </Link>
                    
                    {apiKey ? (
                      <>
                        <Link
                          to="/dashboard"
                          className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted/50"
                        >
                          Dashboard
                        </Link>
                        <Link
                          to="/buy-credits"
                          className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted/50"
                        >
                          <Zap className="w-4 h-4 inline mr-1.5" />
                          Buy Credits
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleLogout}
                          className="ml-2"
                        >
                          <LogOut className="w-4 h-4 mr-1.5" />
                          Logout
                        </Button>
                      </>
                    ) : (
                      <Link to="/buy-credits">
                        <Button size="sm" className="ml-2 gradient-primary text-white border-0 hover:opacity-90 transition-opacity">
                          Get Started
                        </Button>
                      </Link>
                    )}
                  </div>

                  {/* Mobile menu button */}
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="md:hidden p-2 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    {mobileMenuOpen ? (
                      <X className="h-5 w-5" />
                    ) : (
                      <Menu className="h-5 w-5" />
                    )}
                  </button>
                </div>

                {/* Mobile Navigation */}
                {mobileMenuOpen && (
                  <div className="md:hidden border-t border-border/40 bg-background/95 backdrop-blur-md absolute top-16 left-0 right-0 animate-in slide-in">
                    <div className="px-4 py-4 space-y-2">
                      <Link
                        to="/help"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted/50"
                      >
                        <HelpCircle className="w-4 h-4 mr-2" />
                        Help
                      </Link>
                      
                      {apiKey ? (
                        <>
                          <Link
                            to="/dashboard"
                            onClick={() => setMobileMenuOpen(false)}
                            className="block px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted/50"
                          >
                            Dashboard
                          </Link>
                          <Link
                            to="/buy-credits"
                            onClick={() => setMobileMenuOpen(false)}
                            className="flex items-center px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted/50"
                          >
                            <Zap className="w-4 h-4 mr-2" />
                            Buy Credits
                          </Link>
                          <button
                            onClick={handleLogout}
                            className="flex items-center w-full px-4 py-3 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted/50"
                          >
                            <LogOut className="w-4 h-4 mr-2" />
                            Logout
                          </button>
                        </>
                      ) : (
                        <Link
                          to="/buy-credits"
                          onClick={() => setMobileMenuOpen(false)}
                          className="block w-full"
                        >
                          <Button className="w-full gradient-primary text-white border-0">
                            Get Started
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </nav>
            </header>

            {/* Main Content with padding for fixed header */}
            <main className="pt-16">
              <Routes>
                <Route
                  path="/"
                  element={
                    <Home apiKey={apiKey} onSaveApiKey={handleSaveApiKey} />
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <Dashboard
                      apiKey={apiKey}
                      quotaInfo={quotaInfo}
                      loading={loading}
                      onRefresh={fetchQuotaInfo}
                      error={error}
                    />
                  }
                />
                <Route
                  path="/buy-credits"
                  element={<BuyCredits apiKey={apiKey} />}
                />
                <Route path="/pay/success" element={<PaymentSuccess />} />
                <Route path="/pay/cancel" element={<PaymentCancel />} />
                <Route path="/help" element={<Help />} />
              </Routes>
            </main>

            {/* Modern Minimal Footer */}
            <footer className="mt-20 border-t border-border/40 bg-muted/30">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                  <div className="flex items-center space-x-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    <span className="text-sm text-muted-foreground">
                      © {new Date().getFullYear()} RecapMate
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <Link
                      to="/"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Home
                    </Link>
                    <Link
                      to="/help"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Help
                    </Link>
                    <Link
                      to="/buy-credits"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Pricing
                    </Link>
                  </div>
                </div>
              </div>
            </footer>
          </div>
          )}
        </NavigationHandler>
      </HelmetProvider>
    </Router>
  );
}

export default App;
